<template>
  <div class="sales-persons-list-container sales-persons-list-main sales-persons-list-page--glass glass-tab-page">
    <BrucdcumHeader :breadcrumb="['Company', 'Sales Persons']" />

    <div class="glass-tab-view glass-tab-view--flush sales-persons-list-glass-shell">
      <div class="glass-tab-view__content sales-persons-list-tab-content sales-persons-list-page-stack">
        <div class="sales-persons-list-header-block">
          <div class="sales-persons-list-header-row">
            <div class="sales-persons-list-header-row__text">
              <h2>Sales Persons List</h2>
              <p class="sales-persons-list-header-row__sub">
                Manage sales representatives, contact details, and active status
              </p>
            </div>
            <div class="sales-persons-list-header-filter">
              <LabelDropdown
                label="Current View"
                select-id="sales-persons-current-view"
                v-model="currentView"
                inline
                :searchable="false"
                :options="currentViewFilterOptions"
              />
            </div>
            <div class="sales-persons-list-header-actions">
              <SecondaryButton label="Export to CSV" icon="bi bi-download" />
              <PrimaryButton
                label="Add New Sales Person"
                icon="bi bi-plus-circle"
                @click="navigateToAddNewSalesPerson"
              />
              <SecondaryButton
                label="Search"
                icon="bi bi-search"
                @click="showSearchModal = true"
              />
            </div>
          </div>
        </div>

        <div class="sales-persons-list-glass-panel sales-persons-list-main-card">
          <div class="consultant-glass-table-section sales-persons-list-table-section">
            <div
              class="consultant-glass-table-responsive consultant-glass-table-responsive--framed"
              :class="{ 'consultant-glass-table-responsive--loading': loading }"
            >
              <div class="consultant-glass-table-scroll-x">
                <table class="consultant-glass-table sales-persons-list-table">
                  <thead>
                    <tr>
                      <th class="sales-persons-list-col-active">Active</th>
                      <th class="sales-persons-list-col-name">Sales Person</th>
                      <th>Address</th>
                      <th>City</th>
                      <th>State</th>
                      <th>Zip Code</th>
                      <th>Phone</th>
                      <th>Fax</th>
                      <th class="sales-persons-list-col-actions">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="loading">
                      <td colspan="9" class="consultant-glass-table-no-records sales-persons-list-no-records">
                        Loading...
                      </td>
                    </tr>
                    <tr v-else-if="error">
                      <td colspan="9" class="consultant-glass-table-no-records sales-persons-list-no-records">
                        {{ error }}
                      </td>
                    </tr>
                    <tr v-else-if="!filteredSalesPersons.length">
                      <td colspan="9" class="consultant-glass-table-no-records sales-persons-list-no-records">
                        No records to show
                      </td>
                    </tr>
                    <template v-else>
                      <tr
                        v-for="(person, idx) in filteredSalesPersons"
                        :key="person.id"
                        class="consultant-glass-table-row sales-persons-list-row"
                        :style="{ '--spl-row-delay': `${0.04 + Math.min(idx, 12) * 0.03}s` }"
                      >
                        <td class="sales-persons-list-col-active">
                          <input
                            type="checkbox"
                            class="sales-persons-list-active-checkbox"
                            :checked="person.active"
                            disabled
                            aria-label="Active"
                          />
                        </td>
                        <td class="sales-persons-list-col-name">
                          <span class="sales-persons-list-name">{{ person.salesPersonName }}</span>
                        </td>
                        <td>{{ primaryAddressField(person, 'address1') }}</td>
                        <td>{{ primaryAddressField(person, 'city') }}</td>
                        <td>{{ primaryAddressField(person, 'state') }}</td>
                        <td>{{ primaryAddressField(person, 'postalCode') }}</td>
                        <td>{{ person.phone || '—' }}</td>
                        <td>{{ person.fax || '—' }}</td>
                        <td class="sales-persons-list-col-actions">
                          <div class="sales-persons-list-action-buttons" role="group" :aria-label="`Actions for ${person.salesPersonName}`">
                            <SecondaryButton
                              label=""
                              icon="bi bi-pencil-square"
                              aria-label="Edit Sales Person"
                              title="Edit Sales Person"
                              @click="editSalesPerson(person)"
                            />
                          </div>
                        </td>
                      </tr>
                    </template>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <VendorDetailsSearch v-if="showSearchModal" @close="showSearchModal = false" />
  </div>
</template>

<script>
import '../styles/CompanySalesPersonsList.css';
import BrucdcumHeader from '@/components/BrucdcumHeader.vue';
import VendorDetailsSearch from '@/modules/vendor/views/VendorDetailsSearch.vue';
import LabelDropdown from '@/modules/consultant/components/LabelDropdown.vue';
import PrimaryButton from '@/modules/consultant/components/PrimaryButton.vue';
import SecondaryButton from '@/modules/consultant/components/SecondaryButton.vue';
import CompanyService from '@/services/company.service.js';

export default {
  name: 'CompanySalesPersonsList',
  components: {
    BrucdcumHeader,
    VendorDetailsSearch,
    LabelDropdown,
    PrimaryButton,
    SecondaryButton,
  },
  data() {
    return {
      currentView: 'Active',
      showSearchModal: false,
      salesPersons: [],
      loading: false,
      error: null,
      companyId: null,
    };
  },
  computed: {
    currentViewFilterOptions() {
      return [
        { value: 'Active', label: 'Active' },
        { value: 'Inactive', label: 'Inactive' },
        { value: 'All', label: 'All' },
      ];
    },
    filteredSalesPersons() {
      if (this.currentView === 'All') return this.salesPersons;
      const isActive = this.currentView === 'Active';
      return this.salesPersons.filter((person) => person.active === isActive);
    },
  },
  async mounted() {
    this.loading = true;
    this.companyId = this.$route.params.id;
    try {
      this.salesPersons = await CompanyService.getAllSalesPersons(this.companyId);
    } catch (err) {
      this.error = err.message || 'Failed to load sales persons';
    } finally {
      this.loading = false;
    }
  },
  methods: {
    primaryAddressField(person, field) {
      const addr = person.addresses && person.addresses[0];
      if (!addr || !addr[field]) return '—';
      return addr[field];
    },
    navigateToAddNewSalesPerson() {
      const companyId = this.$route.params.id;
      this.$router.push(`/company/${companyId}/dashboard/company/add-salesperson`);
    },
    editSalesPerson(person) {
      const companyId = this.$route.params.id;
      this.$router.push(`/company/${companyId}/dashboard/company/edit-salesperson/${person.id}`);
    },
  },
};
</script>
